import classNames from 'classnames';

import { getRel, getTarget } from '@/theme/shared/utils';

import styles from './LogoAndSocials.module.scss';

import type { FooterData, FooterSocialItem } from '../../types';

type LogoAndSocialsProps = {
  brand?: FooterData['brand'];
  socials: FooterSocialItem[];
  className?: string;
};

export function LogoAndSocials({ brand, socials, className }: LogoAndSocialsProps) {
  if (!brand && socials.length === 0) {
    return null;
  }

  return (
    <div className={classNames(styles.logoAndSocials, className)}>
      <FooterLogo brand={brand} />
      {socials.length > 0 ? (
        <ul className={styles.socialList}>
          {socials.map((social) => (
            <SocialItem key={social.id} social={social} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

interface FooterLogoProps {
  brand?: FooterData['brand'];
}

function FooterLogo({ brand }: FooterLogoProps) {
  if (!brand) {
    return null;
  }

  if (brand.logo?.url) {
    return (
      <a
        className={styles.brandLink}
        href={brand.url || '#'}
        aria-label={brand.name || 'Interact home'}
      >
        <img
          src={brand.logo.url}
          width={brand.logo.width || undefined}
          height={brand.logo.height || undefined}
          alt={brand.logo.alt || brand.name || 'Interact'}
          loading="lazy"
        />
      </a>
    );
  }

  return (
    <a className={styles.brandText} href={brand.url || '#'}>
      {brand.name || 'Interact'}
    </a>
  );
}

interface SocialItemProps {
  social: FooterSocialItem;
}

function SocialItem({ social }: SocialItemProps) {
  const target = getTarget(social.target);

  return (
    <li key={social.id}>
      <a
        className={styles.socialLink}
        href={social.url}
        target={target}
        rel={getRel(target)}
        aria-label={social.label}
      >
        {social.icon?.url ? (
          <img
            src={social.icon.url}
            width={social.icon.width || 32}
            height={social.icon.height || 32}
            alt={social.icon.alt || social.label}
            loading="lazy"
          />
        ) : (
          <span className={styles.socialLabel}>{social.label}</span>
        )}
      </a>
    </li>
  );
}
